/**
 * The navigation data, in two shapes over the same destinations:
 *
 *   - `NavRow`, the one sidebar (PO, 2026-09-25, option A): one row per destination, icon and label, in the
 *     order the sidebar shows them. Collapsed (56 px) it is the icons with a tooltip, expanded (216 px) the icons
 *     and the labels, and every row is one click in either state. A 1 px line parts the three groups (PO,
 *     2026-09-25): Dashboard · the work (Workplace, Dự án, Chiến lược, Tài chính, Đối tác) · the registers and
 *     administration.
 *   - `NavGroup`, the HyperTek prototype's module + pages shape (h-prototype `src/config/navigation.tsx`), kept
 *     for the ⌘K palette. It searches "group label + page label", so "Tổng quan", "Mục tiêu và ưu tiên", "Tổng quan
 *     tài chính" and "Lịch sử thay đổi" are still found by those words, though they left the menu.
 *
 * Row labels are the product's vocabulary (Dashboard, Workplace, Chiến lược, Tài chính, Nhật ký); the landmark
 * tests and the responsive suite look for them.
 *
 * Only pages every holder of the row can open without parameters are listed; a deeper page lights up its row
 * through the longest-prefix match in lib/sidebar-nav.ts — every `/finance/*` page lights up Tài chính.
 */
import {
  Bot, ClipboardList, FolderKanban, Gauge, Handshake, History, LayoutDashboard, ListChecks, ScrollText, ShieldCheck, Target,
  ReceiptText, Settings2, Sparkle, Sparkles, UserCog, Wallet, WalletCards, type LucideIcon,
} from 'lucide-react';
import type { Principal } from '@contracts/common';
import type { Permission } from '@contracts/rbac';
import { hasPermission } from '@domain/rbac';

/** The sidebar's groups, top to bottom; a line parts one from the next. */
export type NavRowGroup = 'home' | 'work' | 'registers';

/** One destination in the sidebar. */
export interface NavRow {
  id: string;
  label: string;
  icon: LucideIcon;
  href: string;
  group: NavRowGroup;
  /** The view permission the row needs, at any scope (RBAC spec §5.3); none means every signed-in member sees it. */
  permission?: Permission;
}

/** The sidebar's destinations, top to bottom; each row shows only to a principal holding its `permission`. */
export const NAV_ROWS: readonly NavRow[] = [
  // Named "Dashboard" (PO, 2026-09-25), as its page is.
  { id: 'dieu-hanh', label: 'Dashboard', icon: LayoutDashboard, href: '/', group: 'home' },
  // Spec §6.1: "Workplace" replaces the old flat task list; `/tasks` only redirects here now. Named as the banner
  // names the page (PO, 2026-09-25), not "Công việc".
  { id: 'cong-viec', label: 'Workplace', icon: ListChecks, href: '/workplace', group: 'work' },
  // Space-scoped projects and their stakeholders (projects & stakeholders spec §6.1).
  { id: 'du-an', label: 'Dự án', icon: FolderKanban, href: '/projects', group: 'work', permission: 'projects.view' },
  { id: 'chien-luoc', label: 'Chiến lược', icon: Target, href: '/strategy', group: 'work', permission: 'strategy.view' },
  // The finance section (finance spec §6): cash flow, projects, entries and settings, all under /finance.
  { id: 'tai-chinh', label: 'Tài chính', icon: Wallet, href: '/finance', group: 'work', permission: 'finance.view' },
  // The company-wide partner directory (projects & stakeholders spec §3.3); it replaced "Người nhận ngoài công ty".
  { id: 'doi-tac', label: 'Đối tác', icon: Handshake, href: '/partners', group: 'work', permission: 'partners.view' },
  { id: 'nhat-ky', label: 'Nhật ký', icon: ScrollText, href: '/activity', group: 'registers', permission: 'audit.view' },
];

/**
 * Administration rows, at the head of the last group, each behind its own permission. The server refuses these
 * routes to anyone else regardless.
 */
export const ADMIN_ROWS: readonly NavRow[] = [
  { id: 'vai-tro', label: 'Vai trò & quyền', icon: ShieldCheck, href: '/admin/roles', group: 'registers', permission: 'roles.view' },
  { id: 'quan-tri-ai', label: 'Quản trị AI', icon: Bot, href: '/admin/ai', group: 'registers', permission: 'ai.configure' },
  { id: 'quan-tri-tai-khoan', label: 'Quản trị tài khoản', icon: UserCog, href: '/admin/users', group: 'registers', permission: 'users.view' },
];

type Reach = Pick<Principal, 'permissions'>;

const reachable = (principal: Reach) => (entry: { permission?: Permission }): boolean =>
  entry.permission === undefined || hasPermission(principal.permissions, entry.permission);

/**
 * "Nhiệm vụ của Hana" (S7 §9.1, P15): the reader's missions, last in the work group. It needs no permission of its own;
 * it shows while Hana is usable to the reader and the admin has missions on, which only the shell knows.
 */
export const MISSIONS_ROW: NavRow = {
  id: 'nhiem-vu-hana', label: 'Nhiệm vụ của trợ lý', icon: Sparkles, href: '/assistant/missions', group: 'work',
};

/**
 * "Trợ lý của tôi" (S8 §9, D90; mockup Màn 3's rail): the reader's own assistant page, closing the work group. Like the
 * missions row it needs no permission; it shows under the same switch as the assistant button, which only the shell
 * knows.
 */
export const ASSISTANT_ME_ROW: NavRow = {
  id: 'tro-ly-cua-toi', label: 'Trợ lý của tôi', icon: Sparkle, href: '/assistant/me', group: 'work',
};

/** The shell's switches for the rows only it can decide: missions on, the assistant usable to the reader. */
export type NavOptions = { missions?: boolean; assistant?: boolean };

const shellRows = (options: NavOptions): NavRow[] => [
  ...(options.missions === true ? [MISSIONS_ROW] : []),
  ...(options.assistant === true ? [ASSISTANT_ME_ROW] : []),
];

/** The sidebar rows this principal can reach, in order: the admin rows open the last group. */
export function navRowsFor(principal: Reach, options: NavOptions = {}): NavRow[] {
  const head = NAV_ROWS.filter((row) => row.group !== 'registers');
  const tail = NAV_ROWS.filter((row) => row.group === 'registers');
  return [...head, ...shellRows(options), ...ADMIN_ROWS, ...tail].filter(reachable(principal));
}

/** A page in the ⌘K palette. */
export interface NavItem {
  id: string;
  label: string;
  icon: LucideIcon;
  href: string;
  /** Opens in a new tab (absolute URL). */
  external?: boolean;
  /** The view permission the page needs, at any scope; none means every signed-in member can open it. */
  permission?: Permission;
}

export interface NavSection {
  id: string;
  /** Optional small uppercase caption above the cluster. */
  label?: string;
  items: NavItem[];
}

/** A module in the ⌘K palette: its label is searched together with each page's. */
export interface NavGroup {
  id: string;
  label: string;
  icon: LucideIcon;
  /** Flat item list. Leave `[]` when using `sections`. */
  items: NavItem[];
  /** Captioned clusters — alternative to a flat `items` list. */
  sections?: NavSection[];
}

/** The palette's modules; each page shows only to a principal holding its `permission`. */
export const NAV_GROUPS: readonly NavGroup[] = [
  {
    id: 'dieu-hanh',
    label: 'Dashboard',
    icon: LayoutDashboard,
    items: [{ id: 'tong-quan', label: 'Tổng quan', icon: Gauge, href: '/' }],
  },
  {
    id: 'cong-viec',
    label: 'Công việc',
    icon: ListChecks,
    items: [
      { id: 'danh-sach-viec', label: 'Workplace', icon: ClipboardList, href: '/workplace' },
    ],
  },
  {
    id: 'du-an',
    label: 'Dự án',
    icon: FolderKanban,
    items: [{ id: 'danh-sach-du-an', label: 'Danh sách dự án', icon: FolderKanban, href: '/projects', permission: 'projects.view' }],
  },
  {
    id: 'chien-luoc',
    label: 'Chiến lược',
    icon: Target,
    items: [{ id: 'muc-tieu', label: 'Mục tiêu và ưu tiên', icon: Target, href: '/strategy', permission: 'strategy.view' }],
  },
  {
    id: 'tai-chinh',
    label: 'Tài chính',
    icon: Wallet,
    items: [
      { id: 'tong-quan-tai-chinh', label: 'Tổng quan tài chính', icon: WalletCards, href: '/finance', permission: 'finance.view' },
      { id: 'tai-chinh-du-an', label: 'Tài chính dự án', icon: FolderKanban, href: '/finance/projects', permission: 'finance.view' },
      { id: 'khoan-thu-chi', label: 'Khoản thu chi', icon: ReceiptText, href: '/finance/entries', permission: 'finance.view' },
      {
        id: 'cai-dat-tai-chinh', label: 'Cài đặt tài chính', icon: Settings2, href: '/finance/settings/balance',
        permission: 'finance.configure',
      },
    ],
  },
  {
    id: 'doi-tac',
    label: 'Đối tác',
    icon: Handshake,
    items: [{ id: 'danh-ba-doi-tac', label: 'Danh bạ Đối tác', icon: Handshake, href: '/partners', permission: 'partners.view' }],
  },
  {
    id: 'nhat-ky',
    label: 'Nhật ký',
    icon: ScrollText,
    items: [{ id: 'lich-su-thay-doi', label: 'Lịch sử thay đổi', icon: History, href: '/activity', permission: 'audit.view' }],
  },
];

/** Administration, for the palette: each page behind its own permission. */
export const ADMIN_GROUP: NavGroup = {
  id: 'quan-tri',
  label: 'Quản trị',
  icon: ShieldCheck,
  items: [
    { id: 'vai-tro', label: 'Vai trò & quyền', icon: ShieldCheck, href: '/admin/roles', permission: 'roles.view' },
    { id: 'quan-tri-ai', label: 'Quản trị AI', icon: Bot, href: '/admin/ai', permission: 'ai.configure' },
    { id: 'quan-tri-tai-khoan', label: 'Quản trị tài khoản', icon: UserCog, href: '/admin/users', permission: 'users.view' },
  ],
};

/**
 * The palette's groups this principal can reach: pages it lacks the permission for are dropped, then empty groups.
 * The shell's switches are the rail's own (`navRowsFor`), so "Nhiệm vụ của trợ lý" and "Trợ lý của tôi" are offered in
 * Công việc exactly where the rail shows them (D158, S8 D90).
 */
export function navGroupsFor(principal: Reach, options: NavOptions = {}): NavGroup[] {
  const can = reachable(principal);
  const own: NavItem[] = shellRows(options).map(({ id, label, icon, href }) => ({ id, label, icon, href }));
  return [...NAV_GROUPS, ADMIN_GROUP]
    .map((group) => ({
      ...group,
      items: [...group.items, ...(group.id === 'cong-viec' ? own : [])].filter(can),
      ...(group.sections
        ? { sections: group.sections.map((section) => ({ ...section, items: section.items.filter(can) })).filter((section) => section.items.length > 0) }
        : {}),
    }))
    .filter((group) => group.items.length > 0 || (group.sections?.length ?? 0) > 0);
}
